import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Divider
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import axiosInstance from '../services/axiosInstance';
import { parseUserImportCsv } from '../utils/userImportCsv';

interface ParsedUser {
  email: string;
  firstName: string;
  lastName: string;
  roles: string;
  isActive: boolean;
  status: 'new' | 'exists' | 'invalid';
  statusMessage?: string;
}

interface ImportUsersDialogProps {
  open: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  availableRoles: string[];
}

const ImportUsersDialog: React.FC<ImportUsersDialogProps> = ({
  open,
  onClose,
  onImportComplete,
  availableRoles,
}) => {
  const [parsedUsers, setParsedUsers] = useState<ParsedUser[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    created: string[];
    skipped: { email: string; reason: string }[];
    errors: { email: string; error: string }[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get('/api/users/template', {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'users-import-template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Kunde inte ladda ner mall');
    }
  };

  const parseCSV = (text: string): ParsedUser[] => {
    return parseUserImportCsv(text).map(parsedRow => {
      const validationErrors = [...parsedRow.errors];

      if (!parsedRow.email.includes('@')) {
        validationErrors.push('Ogiltig e-postadress');
      }

      return {
        email: parsedRow.email,
        firstName: parsedRow.firstName,
        lastName: parsedRow.lastName,
        roles: parsedRow.roles,
        isActive: parsedRow.isActive,
        status: validationErrors.length > 0 ? 'invalid' : 'new',
        statusMessage: validationErrors.length > 0 ? validationErrors.join('. ') : undefined,
      };
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setImportResult(null);

    try {
      const text = await file.text();
      const users = parseCSV(text);
      
      if (users.length === 0) {
        setError('Kunde inte läsa CSV-filen. Kontrollera formatet.');
        return;
      }

      // Check for existing users
      const existingUsers = await axiosInstance.get('/api/users');
      const existingEmails = new Set(existingUsers.data.map((u: any) => u.email.toLowerCase()));

      const usersWithStatus = users.map(user => {
        if (user.status === 'invalid') return user;
        if (existingEmails.has(user.email)) {
          return { ...user, status: 'exists' as const, statusMessage: 'E-postadressen finns redan' };
        }
        return user;
      });

      setParsedUsers(usersWithStatus);
    } catch (err: any) {
      setError('Kunde inte läsa filen: ' + (err.message || 'Okänt fel'));
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    const usersToImport = parsedUsers.filter(u => u.status === 'new');
    if (usersToImport.length === 0) {
      setError('Inga nya användare att importera');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const response = await axiosInstance.post('/api/users/import', {
        users: usersToImport.map(u => ({
          email: u.email,
          firstName: u.firstName,
          lastName: u.lastName,
          roles: u.roles,
          isActive: u.isActive,
        }))
      });

      setImportResult(response.data.results);
      onImportComplete();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Import misslyckades');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setParsedUsers([]);
    setImportResult(null);
    setError(null);
    onClose();
  };

  const newUsersCount = parsedUsers.filter(u => u.status === 'new').length;
  const existingUsersCount = parsedUsers.filter(u => u.status === 'exists').length;
  const invalidUsersCount = parsedUsers.filter(u => u.status === 'invalid').length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Importera användare</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Template download */}
        <Box mb={3}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            size="small"
          >
            Ladda ner CSV-mall
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Fyll i mallen och ladda upp den nedan
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            CSV-mallen och exporten använder semikolon som kolumnseparator för att fungera bättre i Excel.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Flera roller i samma cell separeras med semikolon.
          </Typography>
          {availableRoles.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Tillgängliga roller: {availableRoles.join(', ')}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* File upload */}
        <Box mb={3}>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            ref={fileInputRef}
            id="csv-file-input"
          />
          <label htmlFor="csv-file-input">
            <Button
              variant="contained"
              component="span"
              startIcon={<CloudUploadIcon />}
              disabled={importing}
            >
              Välj CSV-fil
            </Button>
          </label>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Import result */}
        {importResult && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{importResult.created.length}</strong> användare skapade
              {importResult.skipped.length > 0 && (
                <>, <strong>{importResult.skipped.length}</strong> hoppades över</>
              )}
              {importResult.errors.length > 0 && (
                <>, <strong>{importResult.errors.length}</strong> fel</>
              )}
            </Typography>
            {importResult.created.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Skicka inbjudningsmail till de nya användarna från användarlistan.
              </Typography>
            )}
          </Alert>
        )}

        {/* Preview table */}
        {parsedUsers.length > 0 && !importResult && (
          <>
            <Box display="flex" gap={2} mb={2}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${newUsersCount} nya`}
                color="success"
                size="small"
              />
              {existingUsersCount > 0 && (
                <Chip
                  icon={<WarningIcon />}
                  label={`${existingUsersCount} finns redan`}
                  color="warning"
                  size="small"
                />
              )}
              {invalidUsersCount > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${invalidUsersCount} ogiltiga`}
                  color="error"
                  size="small"
                />
              )}
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>E-post</TableCell>
                    <TableCell>Förnamn</TableCell>
                    <TableCell>Efternamn</TableCell>
                    <TableCell>Roller</TableCell>
                    <TableCell>Aktiv</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedUsers.map((user, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        bgcolor: user.status === 'new' ? 'success.50' : 
                                user.status === 'exists' ? 'warning.50' : 'error.50'
                      }}
                    >
                      <TableCell>
                        <Box>
                          {user.status === 'new' && (
                            <Chip label="Ny" color="success" size="small" />
                          )}
                          {user.status === 'exists' && (
                            <Chip label="Finns" color="warning" size="small" />
                          )}
                          {user.status === 'invalid' && (
                            <Chip label="Ogiltig" color="error" size="small" />
                          )}
                          {user.statusMessage && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {user.statusMessage}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.firstName}</TableCell>
                      <TableCell>{user.lastName}</TableCell>
                      <TableCell>{user.roles}</TableCell>
                      <TableCell>{user.isActive ? 'Ja' : 'Nej'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {importResult ? 'Stäng' : 'Avbryt'}
        </Button>
        {!importResult && parsedUsers.length > 0 && newUsersCount > 0 && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing}
            startIcon={importing ? <CircularProgress size={20} /> : null}
          >
            {importing ? 'Importerar...' : `Importera ${newUsersCount} användare`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportUsersDialog;

